import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import {
  QuestionAnsweringClientContext,
  TaskQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { PersonId } from "../../../models/person";
import { Question, QuestionAnswer } from "../../../models/questionnaire";
import { Coding } from "../../../models/structuration";
import { SearchBar } from "../../../shared/components/search/SearchBar";
import { DisplayAnswerFacts } from "../../../shared/components/ui/QuestionAnswering/DisplayAnswerFacts";

type QuestionAnsweringProps = {
  personIds: PersonId[];
};

export const QuestionAnswering = ({ personIds }: QuestionAnsweringProps) => {
  // Context
  const { showSnackbar } = useSnackbar();
  const questionAnsweringClient = React.useContext(QuestionAnsweringClientContext);
  const taskQuartzClient = React.useContext(TaskQuartzClientContext);

  // State
  const [questionsAnswers, setQuestionsAnswers] = React.useState<QuestionAnswer[]>([]);

  // Handlers
  const handleSearch = async (coding: Coding) => {
    const question = { main_concept: [coding] } as Question;
    try {
      let task = await questionAnsweringClient.askQuestions([question], personIds);
      let taskCompleted = false;
      while (!taskCompleted) {
        try {
          task = await taskQuartzClient.getTaskById(task.task_id);
          if (task.status === "completed") {
            const results = task.output;
            setQuestionsAnswers((prev) => [...prev, ...results]);
            taskCompleted = true;
          } else if (task.status === "failed") {
            showSnackbar({ text: "Task failed", color: "danger" });
            taskCompleted = true;
          }
        } catch (e: any) {
          showSnackbar({ text: e.message, color: "danger" });
          taskCompleted = true;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Poll every second
      }
    } catch (e: any) {
      console.error(e.message);
    }
  };

  const handleRemove = (index: number) => {
    setQuestionsAnswers((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={2} direction="column" sx={{ mb: 2 }}>
      <SearchBar
        onChange={(coding) => coding && handleSearch(coding)}
        message="Search patient records"
      />
      {questionsAnswers.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg">Search results</Typography>
            <Box>
              <List>
                {questionsAnswers.map((questionAnswers, index) => (
                  <ListItem
                    key={`${questionAnswers.text}-${questionAnswers.main_concept
                      .map((c) => c.code)
                      .join(",")}-${index}`}
                  >
                    <ListItemButton variant="plain">
                      <ListItemDecorator>
                        <IconButton color="neutral" size="sm" onClick={() => handleRemove(index)}>
                          <CloseIcon />
                        </IconButton>
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography>
                          <Typography level="title-sm">{questionAnswers.text} </Typography>
                          <Typography level="body-xs">
                            {questionAnswers.main_concept.map((c) => c.code).join(",")}
                          </Typography>
                        </Typography>
                        <Typography>
                          <DisplayAnswerFacts
                            answerType={questionAnswers.answer_type || "concept-date"}
                            answers={questionAnswers.answer}
                          />
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};
